import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import * as Cmp from "@impero-com/common/utils/comparison";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import { PartialControlForUserListDto, StepIndex } from "api/generated/model";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidTagsSelectFilter } from "components/table/tsTable/TSTValidTagsSelectFilter";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import {
    formatTagsAndCategoriesString,
    tagAndCategoryListDefaultTSTCell,
    tagsTSTFilterFn,
    tagsTSTSortingFn,
} from "org/tags/tagsTSTUtils";
import { WebRoute } from "routes";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import { localizeAssignedAs } from "../utils/localization";

import {
    CONTROL_LIST_COLUMN_IDS,
    CONTROL_TABLE_STATE_STORAGE_KEY,
    controlListColumnFiltersCodecMap,
    controlListTableDefaultValues,
} from "./consts";

export interface ControlRow {
    id: string;
    controlId: string;
    controlTitle: string;
    controlProgramName: string;
    controlProgramUrl: string;
    hasAccess: boolean;
    assignmentGroupId: string;
    stepIndex: StepIndex;
    assignedAs: string;
    tagIds: string[];
}

const SUBSEQUENT_STEP_INDEX: Record<string, StepIndex> = {
    SubsequentStep0: "Step1",
    SubsequentStep1: "Step2",
};

function buildRows(
    t: TFunction,
    data: PartialControlForUserListDto
): ControlRow[] {
    const { controlPrograms } = data.linkedResources;

    return data.content.flatMap(({ resource: control, actions }) => {
        const programDto = controlPrograms.find(
            (dto) => dto.resource.id === control.controlProgramId
        );
        const toRow = (assignmentGroupId: string, stepIndex: StepIndex) => ({
            controlId: control.id,
            controlTitle: control.title,
            controlProgramName: programDto?.resource.name ?? "",
            controlProgramUrl: buildControlProgramViewUrl(
                control.controlProgramId
            ),
            hasAccess: actions.view,
            id: `${control.id}-${assignmentGroupId}-${stepIndex}`,
            assignmentGroupId,
            stepIndex,
            assignedAs: localizeAssignedAs(t, stepIndex),
            tagIds: control.tagIds[assignmentGroupId] ?? [],
        });

        const rows: ControlRow[] = [];
        // The responsible assignment (Step0) lives in `initialStep`; only
        // individual user assignments are listed, not groups/pools.
        Object.entries(control.stepGroup.initialStep).forEach(
            ([assignmentGroupId, assignment]) => {
                if (assignment.type === "User") {
                    rows.push(toRow(assignmentGroupId, "Step0"));
                }
            }
        );
        // Reviewer / second reviewer assignments live in `subsequentSteps`.
        Object.entries(control.stepGroup.subsequentSteps).forEach(
            ([subsequentStepIndex, assignments]) => {
                const stepIndex = SUBSEQUENT_STEP_INDEX[subsequentStepIndex];
                Object.entries(assignments ?? {}).forEach(
                    ([assignmentGroupId, assignment]) => {
                        if (assignment.type === "User") {
                            rows.push(toRow(assignmentGroupId, stepIndex));
                        }
                    }
                );
            }
        );
        return rows;
    });
}

export function useUserAssignedControlListTable(
    data: PartialControlForUserListDto
) {
    const { t } = useTranslation();

    const tableData = useMemo(() => buildRows(t, data), [t, data]);
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        CONTROL_TABLE_STATE_STORAGE_KEY,
        controlListTableDefaultValues,
        controlListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t, data);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => row.id,
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<ControlRow>(),
        getSortedRowModel: getSortedRowModel<ControlRow>(),
        getFilteredRowModel: getFilteredRowModel<ControlRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<ControlRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<ControlRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (
        column: Column<ControlRow>,
        target: FilterTarget
    ) => getFilterComponent(t, column, target, data);

    return {
        table,
        getFilterComponent: filterComponent,
        canAddException: (column: Column<ControlRow>) =>
            column.id === CONTROL_LIST_COLUMN_IDS.TAGS,
        tableId: CONTROL_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<ControlRow>();
function buildColumns(t: TFunction, data: PartialControlForUserListDto) {
    const { tags, tagCategories } = data.linkedResources;
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => (
                <Checkbox
                    checked={info.table.getIsAllPageRowsSelected()}
                    indeterminate={info.table.getIsSomePageRowsSelected()}
                    onChange={info.table.getToggleAllRowsSelectedHandler()}
                />
            ),
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => (
                <Checkbox
                    checked={info.row.getIsSelected()}
                    onClick={() => info.row.toggleSelected()}
                />
            ),
        }),
        columnHelper.accessor((row) => row.controlTitle, {
            id: CONTROL_LIST_COLUMN_IDS.CONTROL,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        href={`${WebRoute.CONTROLS}/${row.controlId}`}
                        hasAccess={row.hasAccess}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        CONTROL_LIST_COLUMN_IDS.CONTROL,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_Controls_Control"),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor((row) => row.controlProgramName, {
            id: CONTROL_LIST_COLUMN_IDS.CONTROL_PROGRAM,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        href={row.controlProgramUrl}
                        hasAccess={row.hasAccess}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        CONTROL_LIST_COLUMN_IDS.CONTROL_PROGRAM,
                        value
                    )
            ),
            header: () => t("ControlProgram"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.assignedAs, {
            id: CONTROL_LIST_COLUMN_IDS.ASSIGNED_AS,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: Cmp.map((row) => row.original.stepIndex),
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlRow, string[]>(
                (row, value) => value.includes(row.original.stepIndex)
            ),
            header: () => t("AccessAndAssignment_Controls_AssignedAs"),
            size: TST_COLUMN_SIZE.L,
        }),
        columnHelper.accessor(
            (row) =>
                formatTagsAndCategoriesString(row.tagIds, {
                    tags,
                    tagCategories,
                }),
            {
                id: CONTROL_LIST_COLUMN_IDS.TAGS,
                cell: (info) =>
                    tagAndCategoryListDefaultTSTCell(
                        info.row.original.tagIds,
                        tags,
                        tagCategories
                    ),
                enableSorting: true,
                sortingFn: (rowA, rowB) =>
                    tagsTSTSortingFn(
                        rowA.original.tagIds,
                        rowB.original.tagIds
                    ),
                enableColumnFilter: true,
                filterFn: createFilterFnWithMode<ControlRow, string[]>(
                    (row, value) => tagsTSTFilterFn(row.original.tagIds, value)
                ),
                header: () => t("AccessAndAssignment_Controls_Tags"),
                size: TST_COLUMN_SIZE.L,
            }
        ),
    ];
}

type ControlListColumnFilters = ZodCodecMapToType<
    typeof controlListColumnFiltersCodecMap
>;

type ControlFilterableColumnId = keyof ControlListColumnFilters;

function getUniqueTagIds(column: Column<ControlRow>): string[] {
    const tagIdsPerRow = column
        .getFacetedRowModel()
        .flatRows.map((row) => row.original.tagIds);
    return Array.from(new Set(tagIdsPerRow.flat()));
}

function getFilterComponent(
    t: TFunction,
    column: Column<ControlRow>,
    target: FilterTarget,
    data: PartialControlForUserListDto
) {
    const getFilterHelpers =
        createFilterHelpersGetter<ControlListColumnFilters>(column);
    const columnId = column.id as ControlFilterableColumnId;

    switch (columnId) {
        case CONTROL_LIST_COLUMN_IDS.CONTROL:
        case CONTROL_LIST_COLUMN_IDS.CONTROL_PROGRAM: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((option) => ({
                label: option,
                value: option,
                customLabel: <CustomEllipsisLabel text={option} />,
            }));
            const placeholderKey = {
                [CONTROL_LIST_COLUMN_IDS.CONTROL]:
                    "AccessAndAssignment_Controls_Control",
                [CONTROL_LIST_COLUMN_IDS.CONTROL_PROGRAM]: "ControlProgram",
            } as const;
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(placeholderKey[columnId])}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_LIST_COLUMN_IDS.ASSIGNED_AS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const stepIndexes = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.stepIndex)
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...stepIndexes].map((stepIndex) => ({
                        label: localizeAssignedAs(t, stepIndex),
                        value: stepIndex,
                    }))}
                    placeholder={t("AccessAndAssignment_Controls_AssignedAs")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_LIST_COLUMN_IDS.TAGS: {
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={data.linkedResources.tagCategories}
                    tags={data.linkedResources.tags}
                    uniqueTagIds={getUniqueTagIds(column)}
                    placeholder={t("AccessAndAssignment_Controls_Tags")}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
